import { fromEmpty } from 'maybeasy';
import { always } from '@kofno/piper';
import { FilterOptionData } from '../Types';
import { when } from '@execonline-inc/maybe-adapter';
import { equalArrays, exclude, include, includes } from '../../../../../../../Collections';

export type GeneralFilterOptions = SomeOptions | AllOptions | NoOption;
export type DurationFilterOptions = SomeDurationOptions | AllDurationOptions | NoDurationOption;
export type FilterOptions = GeneralFilterOptions | DurationFilterOptions;

export type SomeOptions = {
  kind: 'some-options';
  label: string;
  value: string;
  count: number;
};

export type AllOptions = {
  kind: 'all-options';
  label: 'All';
  value: 'All';
  count: number;
};

export type NoOption = {
  kind: 'no-option';
  label: 'None';
  value: 'None';
  count: 'None';
};

export type SomeDurationOptions = {
  kind: 'some-hours';
  label: string;
  value: string;
  count: number;
};

export type AllDurationOptions = {
  kind: 'all-hours';
  label: 'All';
  value: 'All';
  count: number;
};

export type NoDurationOption = {
  kind: 'no-hour';
  label: 'None';
  value: string;
  count: number;
};

export type Filter = {
  options: ReadonlyArray<string>;
};

export interface Opened {
  kind: 'opened';
  filter: Filter;
}

export interface Closed {
  kind: 'closed';
  filter: Filter;
}

export type State = Opened | Closed;

export const opened = (state: Closed): Opened => ({
  kind: 'opened',
  filter: { ...state.filter },
});

export const blank = (filter: Filter): State => ({
  kind: 'closed',
  filter,
});

export const closed = (state: Opened): Closed => ({
  kind: 'closed',
  filter: { ...state.filter },
});

const allOptionsFilter = (
  options: ReadonlyArray<string>,
  initialFilterValues: ReadonlyArray<string>,
): ReadonlyArray<string> =>
  when(equalArrays(options, initialFilterValues), true)
    .map<readonly string[]>(always([]))
    .getOrElseValue(initialFilterValues);

export const updatingSelectedOption = (
  state: Opened,
  filterValue: FilterOptions,
  initialFilterValues: Filter['options'],
): Opened => {
  const { options } = state.filter;
  switch (filterValue.kind) {
    case 'all-options':
    case 'all-hours':
      return {
        kind: 'opened',
        filter: {
          options: allOptionsFilter(options, initialFilterValues),
        },
      };
    case 'no-option':
    case 'some-options':
    case 'no-hour':
    case 'some-hours':
      const buildList = includes(filterValue.value, options)
        ? exclude(filterValue.value)
        : include(filterValue.value);

      return {
        kind: 'opened',
        filter: {
          options: buildList(options),
        },
      };
  }
};

export const allOptions = (options: FilterOptionData[]): GeneralFilterOptions[] => [
  {
    kind: 'all-options',
    label: 'All',
    value: 'All',
    count: options.length,
  },
  ...getOptions(options),
];

const noOption = (): GeneralFilterOptions => ({
  kind: 'no-option',
  label: 'None',
  value: 'None',
  count: 'None',
});

const someOptions = (data: FilterOptionData): GeneralFilterOptions => ({
  kind: 'some-options',
  label: data.label,
  value: data.value,
  count: data.count,
});

const filterObject = (object: FilterOptionData): GeneralFilterOptions =>
  fromEmpty(object.value)
    .map(() => someOptions(object))
    .getOrElse(noOption);

const getOptions = (options: FilterOptionData[]): GeneralFilterOptions[] =>
  options.map(filterObject);

export const allDurationOptions = (options: FilterOptionData[]): DurationFilterOptions[] => [
  {
    kind: 'all-hours',
    label: 'All',
    value: 'All',
    count: options.length,
  },
  ...getDurationOptions(options),
];

const noDuration = (data: FilterOptionData): DurationFilterOptions => ({
  kind: 'no-hour',
  label: 'None',
  value: data.value,
  count: data.count,
});

const someDuration = (data: FilterOptionData): DurationFilterOptions => ({
  kind: 'some-hours',
  label: data.label,
  value: data.value,
  count: data.count,
});

const hasDuration = (filterOption: FilterOptionData): boolean => Number(filterOption.value) > 0;

const durationFilterObject = (option: FilterOptionData): DurationFilterOptions =>
  when(hasDuration, option)
    .map(someDuration)
    .getOrElse(() => noDuration(option));

const getDurationOptions = (options: FilterOptionData[]): DurationFilterOptions[] =>
  options.map(durationFilterObject);
